/**
 * Componente AddressAutocomplete
 * Integração com Google Maps Places API (New) para autocomplete de endereços
 * Versão simplificada com estilos fixos que não são afetados pelo tema
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { Form } from "antd";
import { useGoogleMaps } from "../../contexts/GoogleMapsContext";

const AddressAutocomplete = ({
  label,
  value,
  onChange,
  placeholder = "Digite um endereço",
  required = false,
  error,
  helperText,
  className,
  id,
  onAddressSelect,
  debounceDelay = 300,
  noFormItem = false,
  ...props
}) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const [inputValue, setInputValue] = useState(value || "");
  const [isInitialized, setIsInitialized] = useState(false);
  const [showGoogleInput, setShowGoogleInput] = useState(false);

  const autocompleteElementRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const isInitializingRef = useRef(false);
  const cleanupRef = useRef(null);

  useEffect(() => {
    // Só atualiza se o valor realmente mudou e não foi uma mudança interna
    const newValue = value || "";
    if (newValue !== inputValue) {
      setInputValue(newValue);
      if (inputRef.current) {
        inputRef.current.value = newValue;
      }
    }
  }, [value]);

  const handleFocus = useCallback(() => {
    // O Google Maps já está sendo carregado pelo contexto global
    // Não precisa fazer nada aqui
  }, []);

  // Cleanup effect para limpar recursos quando o componente desmontar
  useEffect(() => {
    return () => {
      if (autocompleteElementRef.current) {
        try {
          const element = autocompleteElementRef.current;
          // Remove event listeners se existirem
          if (element.removeEventListener) {
            element.removeEventListener("gmp-placeselect", () => {});
            element.removeEventListener("gmp-select", () => {});
            element.removeEventListener("gmp-placechange", () => {});
            element.removeEventListener("gmp-error", () => {});
          }
        } catch (e) {
          // Ignora erros de cleanup
        }
        autocompleteElementRef.current = null;
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
      isInitializingRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !containerRef.current || loadError || isInitializingRef.current) {
      return;
    }

    // Evita múltiplas inicializações simultâneas
    if (isInitializingRef.current) {
      return;
    }

    isInitializingRef.current = true;

    const initializeAutocomplete = async () => {
      try {
        // Aguarda o Google Maps estar totalmente carregado
        let attempts = 0;
        const maxAttempts = 20;
        while (!window.google?.maps && attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }

        if (!window.google?.maps) {
          console.error("Google Maps API não foi carregada corretamente");
          isInitializingRef.current = false;
          return;
        }

        // Garante que a biblioteca Places está carregada
        if (!window.google.maps.places) {
          try {
            await window.google.maps.importLibrary("places");
            // Aguarda um pouco mais após importar para garantir que está totalmente pronto
            await new Promise(resolve => setTimeout(resolve, 300));
          } catch (importError) {
            console.error("Erro ao importar biblioteca Places:", importError);
            isInitializingRef.current = false;
            return;
          }
        }

        // Verifica se a biblioteca Places está realmente disponível
        if (!window.google?.maps?.places) {
          console.error("Biblioteca Places não está disponível após importação");
          isInitializingRef.current = false;
          return;
        }

        // Aguarda o custom element estar definido antes de criar a instância
        let customElementDefined = false;
        try {
          await customElements.whenDefined("gmp-place-autocomplete");
          customElementDefined = true;
        } catch (defineError) {
          // Se o elemento ainda não estiver definido, aguarda um pouco mais
          console.warn("Aguardando registro do custom element...");
          let attempts = 0;
          const maxAttempts = 10;
          while (!customElementDefined && attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 100));
            try {
              await customElements.whenDefined("gmp-place-autocomplete");
              customElementDefined = true;
            } catch (e) {
              attempts++;
            }
          }
        }

        // Verifica novamente se o PlaceAutocompleteElement está disponível
        if (!window.google?.maps?.places?.PlaceAutocompleteElement) {
          console.warn("PlaceAutocompleteElement não está disponível.");
          isInitializingRef.current = false;
          return;
        }

        // Verifica se o construtor está disponível
        const PlaceAutocompleteConstructor = window.google.maps.places.PlaceAutocompleteElement;
        if (typeof PlaceAutocompleteConstructor !== 'function') {
          console.error("PlaceAutocompleteElement não é um construtor válido");
          isInitializingRef.current = false;
          return;
        }

        containerRef.current.innerHTML = "";

        let autocompleteElement;
        try {
          // Aguarda um pouco mais para garantir que o custom element está totalmente pronto
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Verifica se o Google Maps está realmente pronto antes de criar o elemento
          if (!window.google?.maps?.places?.PlaceAutocompleteElement) {
            console.error("PlaceAutocompleteElement não está disponível após aguardar");
            isInitializingRef.current = false;
            return;
          }
          
          // Tenta criar o elemento usando o construtor
          try {
            autocompleteElement = new PlaceAutocompleteConstructor({
              componentRestrictions: { country: "br" },
            });
          } catch (constructorError) {
            console.warn("Erro ao criar com construtor, tentando via DOM:", constructorError);
            // Se o construtor falhar, tenta criar o elemento diretamente no DOM
            try {
              autocompleteElement = document.createElement("gmp-place-autocomplete");
              if (autocompleteElement) {
                autocompleteElement.setAttribute("component-restrictions", JSON.stringify({ country: "br" }));
              } else {
                throw new Error("Não foi possível criar elemento via DOM");
              }
            } catch (domError) {
              console.error("Erro ao criar elemento via DOM:", domError);
              throw constructorError; // Re-lança o erro original
            }
          }
        } catch (createError) {
          console.error("Erro ao criar PlaceAutocompleteElement:", createError);
          console.error("Detalhes do erro:", {
            error: createError.message,
            stack: createError.stack,
            customElementDefined,
            constructorAvailable: typeof PlaceAutocompleteConstructor === 'function',
            googleMapsReady: !!window.google?.maps,
            placesReady: !!window.google?.maps?.places
          });
          isInitializingRef.current = false;
          setIsInitialized(false);
          return;
        }

        // Verifica se o container ainda está disponível antes de adicionar o elemento
        if (!containerRef.current) {
          console.warn("Container não está disponível, abortando inicialização");
          isInitializingRef.current = false;
          setIsInitialized(false);
          return;
        }

        if (placeholder) {
          autocompleteElement.setAttribute("placeholder", placeholder);
        }

        containerRef.current.appendChild(autocompleteElement);
        autocompleteElementRef.current = autocompleteElement;

        // Aguarda o custom element estar totalmente definido e pronto
        try {
          await customElements.whenDefined("gmp-place-autocomplete");
        } catch (e) {
          // Se já estiver definido, continua normalmente
        }
        
        // Aguarda um pouco mais para garantir que o elemento está totalmente inicializado
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Verifica se o container e elemento ainda estão disponíveis
        if (!containerRef.current || !autocompleteElementRef.current) {
          console.warn("Container ou elemento não está mais disponível após aguardar");
          isInitializingRef.current = false;
          setIsInitialized(false);
          return;
        }

        // Verifica se o elemento ainda está no DOM e válido
        if (!autocompleteElementRef.current.isConnected || !containerRef.current.contains(autocompleteElementRef.current)) {
          console.warn("Elemento autocomplete não está mais conectado ao DOM");
          isInitializingRef.current = false;
          setIsInitialized(false);
          return;
        }

        let inputElement = autocompleteElementRef.current.querySelector("input");
        let inputAttempts = 0;
        const maxInputAttempts = 10;
        
        while (!inputElement && inputAttempts < maxInputAttempts) {
          await new Promise(resolve => setTimeout(resolve, 50));
          // Verifica novamente se o elemento ainda está disponível
          if (!autocompleteElementRef.current) {
            break;
          }
          inputElement = autocompleteElementRef.current.querySelector("input");
          inputAttempts++;
        }
        
        if (inputElement) {
          if (inputValue) {
            inputElement.value = inputValue;
            inputElement.dispatchEvent(new Event("input", { bubbles: true }));
            inputElement.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }

        const handleSelect = async (event) => {
          try {
            if (!event || !event.detail) return;

            const place = event.detail;
            let placeObj = place;
            
            // Verifica se o Google Maps ainda está disponível
            if (!window.google?.maps?.places) {
              console.error("Google Maps Places não está disponível durante handleSelect");
              return;
            }
            
            if (event.placePrediction && typeof event.placePrediction.toPlace === 'function') {
              try {
                placeObj = await event.placePrediction.toPlace();
              } catch (toPlaceError) {
                console.error("Erro ao converter placePrediction:", toPlaceError);
                placeObj = place;
              }
            }
            
            if (placeObj && typeof placeObj.fetchFields === 'function') {
              try {
                await placeObj.fetchFields({
                  fields: ["formattedAddress", "addressComponents", "location", "id"],
                });
              } catch (fetchError) {
                console.error("Erro ao buscar campos do lugar:", fetchError);
                // Continua mesmo se fetchFields falhar, usando os dados disponíveis
              }
            }

            const formattedAddress = placeObj?.formattedAddress || placeObj?.formatted_address;
            if (!formattedAddress) return;

            const addressComponents = placeObj.addressComponents || placeObj.address_components || [];
            
            let rua = "";
            let numero = "";
            let bairro = "";
            let cep = "";
            let complemento = "";

            addressComponents.forEach((component) => {
              const types = component.types;
              if (types.includes("street_number")) {
                numero = component.longText || component.shortText || component.long_name || "";
              } else if (types.includes("route")) {
                rua = component.longText || component.shortText || component.long_name || "";
              } else if (types.includes("sublocality") || types.includes("sublocality_level_1") || types.includes("neighborhood")) {
                bairro = component.longText || component.shortText || component.long_name || "";
              } else if (types.includes("postal_code")) {
                cep = (component.longText || component.shortText || component.long_name || "").replace(/\D/g, "");
              }
            });

            if (!rua && formattedAddress) {
              const parts = formattedAddress.split(",");
              if (parts.length > 0) {
                const firstPart = parts[0].trim();
                const match = firstPart.match(/^(\d+)\s*(.+)$/);
                if (match) {
                  numero = match[1];
                  rua = match[2];
                } else {
                  rua = firstPart;
                }
              }
            }

            if (!bairro && formattedAddress) {
              const parts = formattedAddress.split(",");
              if (parts.length > 1) {
                bairro = parts[1].trim();
              }
            }

            const location = placeObj.location || placeObj.geometry?.location;
            const coords = location
              ? {
                  lat: typeof location.lat === 'function' ? location.lat() : location.lat,
                  lng: typeof location.lng === 'function' ? location.lng() : location.lng,
                }
              : undefined;

            setInputValue(formattedAddress);
            
            // Chama onChange primeiro para atualizar o Form
            if (onChange) {
              onChange(formattedAddress);
            }

            // Depois chama onAddressSelect se fornecido
            if (onAddressSelect) {
              onAddressSelect({
                enderecoCompleto: formattedAddress,
                rua,
                numero,
                bairro,
                cep,
                complemento,
                coords,
                placeId: placeObj.id || placeObj.place_id,
              });
            }
          } catch (error) {
            console.error("Erro ao processar endereço selecionado:", error);
          }
        };

        // Adiciona event listeners com tratamento de erros
        const addEventListenerSafe = (element, event, handler) => {
          try {
            element.addEventListener(event, handler);
          } catch (error) {
            console.error(`Erro ao adicionar listener para ${event}:`, error);
          }
        };

        addEventListenerSafe(autocompleteElement, "gmp-placeselect", handleSelect);
        addEventListenerSafe(autocompleteElement, "gmp-select", handleSelect);
        addEventListenerSafe(autocompleteElement, "gmp-placechange", handleSelect);
        
        // Adiciona listener para erros de rede
        addEventListenerSafe(autocompleteElement, "gmp-error", (errorEvent) => {
          console.warn("Erro de rede no PlaceAutocomplete:", errorEvent);
          // Não impede o funcionamento, apenas registra o erro
        });

        let inputChangeTimeout;
        if (inputElement) {
          const handleInputChange = (e) => {
            const newValue = e.target.value;
            setInputValue(newValue);
            
            // Chama onChange imediatamente para componentes controlados (como Form.Item)
            // mas também mantém o debounce para evitar muitas atualizações
            if (onChange) {
              // Para componentes controlados, chama imediatamente
              onChange(newValue);
            }
            
            // Mantém o debounce apenas para outras operações se necessário
            clearTimeout(inputChangeTimeout);
            inputChangeTimeout = setTimeout(() => {
              // Pode ser usado para outras operações assíncronas se necessário
            }, debounceDelay);
          };
          inputElement.addEventListener("input", handleInputChange);
        }

        // Aguarda um pouco mais para garantir que o input do Google Maps está renderizado
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Verifica se o input do Google Maps foi criado
        const googleInput = autocompleteElementRef.current?.querySelector("input");
        if (googleInput) {
          setShowGoogleInput(true);
          if (inputRef.current) {
            inputRef.current.style.display = 'none';
            inputRef.current.style.visibility = 'hidden';
            inputRef.current.style.position = 'absolute';
            inputRef.current.style.width = '0';
            inputRef.current.style.height = '0';
            inputRef.current.style.opacity = '0';
            inputRef.current.style.pointerEvents = 'none';
          }
        }
        setIsInitialized(true);

        // Armazena função de cleanup para uso quando o componente desmontar
        cleanupRef.current = () => {
          clearTimeout(inputChangeTimeout);
          if (autocompleteElementRef.current) {
            try {
              autocompleteElementRef.current.removeEventListener("gmp-placeselect", handleSelect);
              autocompleteElementRef.current.removeEventListener("gmp-select", handleSelect);
              autocompleteElementRef.current.removeEventListener("gmp-placechange", handleSelect);
              autocompleteElementRef.current.removeEventListener("gmp-error", () => {});
            } catch (e) {
              // Ignora erros de cleanup
            }
          }
        };
      } catch (error) {
        console.error("Erro ao inicializar PlaceAutocompleteElement:", error);
        isInitializingRef.current = false;
        setIsInitialized(false);
      }
    };

    initializeAutocomplete().catch((error) => {
      console.error("Erro fatal ao inicializar autocomplete:", error);
      isInitializingRef.current = false;
      setIsInitialized(false);
    });
  }, [isLoaded, loadError, placeholder, onChange, onAddressSelect, debounceDelay, inputValue]);

  // Determina qual input mostrar: nativo se não estiver inicializado, autocomplete se estiver
  // Usa showGoogleInput para garantir que o input do Google Maps está realmente renderizado
  const showNativeInput = !showGoogleInput && (!isInitialized || !isLoaded || loadError);
  const showAutocomplete = showGoogleInput && isInitialized && isLoaded && !loadError;

  const nativeInput = (
    <input
      ref={inputRef}
      type="text"
      value={inputValue}
      onChange={(e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        if (onChange) {
          onChange(newValue);
        }
      }}
      onFocus={handleFocus}
      placeholder={placeholder}
      style={{ 
        width: '100%',
        display: showNativeInput ? 'block' : 'none',
        position: showNativeInput ? 'relative' : 'absolute',
        visibility: showNativeInput ? 'visible' : 'hidden',
        opacity: showNativeInput ? 1 : 0,
        height: showNativeInput ? 'auto' : 0,
        overflow: 'hidden',
        pointerEvents: showNativeInput ? 'auto' : 'none',
      }}
      {...props}
    />
  );

  const autocompleteContainer = (
    <div 
      ref={containerRef} 
      style={{ 
        display: showAutocomplete ? 'block' : 'none',
        width: '100%',
        position: 'relative',
      }}
    />
  );

  const inputElement = (
    <div style={{ position: 'relative', width: '100%' }}>
      {nativeInput}
      {autocompleteContainer}
      {loadError && (
        <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
          Erro ao carregar Google Maps. Verifique a chave da API.
        </div>
      )}
    </div>
  );

  if (label && !noFormItem) {
    return (
      <Form.Item
        label={label}
        required={required}
        validateStatus={error ? "error" : undefined}
        help={error || helperText}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        {inputElement}
      </Form.Item>
    );
  }

  return inputElement;
};

export default AddressAutocomplete;
